<template>
  <div :class="embedded ? '' : 'container-fluid mt-4 px-3'">
    <div :class="embedded ? '' : 'card'">
      <div
        v-if="!embedded"
        class="card-header card-header-primary d-flex align-items-center gap-2 flex-wrap"
      >
        <i class="fab fa-telegram-plane" aria-hidden="true"></i>
        <h4 class="card-title mb-0">{{ $t("telegramAi.telegramParametersTitle") }}</h4>
      </div>
      <div :class="embedded ? '' : 'card-body'">
        <ContentSkeleton v-if="loading" variant="form" :rows="4" />
        <template v-else>
          <div v-if="!cfg.moduleActive" class="alert alert-warning mb-3">
            {{ $t("telegramAi.moduleInactive") }}
          </div>
          <div v-if="cfg.moduleActive && !cfg.billingFeatureEnabled" class="alert alert-warning mb-3">
            {{ $t("telegramAiBilling.deactivated") }}
          </div>
          <div v-if="cfg.moduleActive && !cfg.allowlistConfigured" class="alert alert-warning mb-3">
            {{ $t("telegramAi.allowlistEmpty") }}
          </div>
          <div class="alert alert-secondary small mb-3">
            {{ $t("telegramAi.settingsBotManagedInBilling") }}
          </div>
          <telegram-ai-allowed-users-select
            v-model="allowedSystemUsers"
            :options="pickerUsers"
            :loading="loading"
            :disabled="saving"
            :label="$t('telegramAi.allowedUserIds')"
            :hint="$t('telegramAi.allowedUsersHint')"
            input-id="tgAllowlist"
          />
          <div class="form-group mb-3">
            <label class="d-block" for="tgWebhookBase">{{
              $t("telegramAi.webhookPublicBaseOptional")
            }}</label>
            <input
              id="tgWebhookBase"
              v-model="webhookPublicBaseOverride"
              type="url"
              class="form-control font-monospace small"
              autocomplete="off"
              :placeholder="$t('telegramAi.webhookPublicBasePlaceholder')"
              :disabled="saving"
            />
            <small class="text-muted d-block mt-1">{{
              $t("telegramAi.webhookPublicBaseHintCustomBot")
            }}</small>
          </div>
          <div class="d-flex flex-wrap gap-2">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="saving"
              @click="save"
            >
              <span v-if="saving">
                <i class="fas fa-spinner fa-spin me-1" aria-hidden="true"></i>
              </span>
              {{ $t("telegramAi.save") }}
            </button>
          </div>

          <div class="border-top pt-3 mt-4">
            <h5 class="h6 mb-2">{{ $t("telegramAi.aiCaptureExportTitle") }}</h5>
            <p class="small text-muted mb-2">
              {{
                $t("telegramAi.aiCaptureExportHint", {
                  days: aiCaptureMeta.retentionDays,
                })
              }}
            </p>
            <p v-if="cfg.tenantDb" class="small text-muted mb-2">
              {{
                $t("telegramAi.aiCaptureExportTenant", { tenant: cfg.tenantDb })
              }}
            </p>
            <p class="small mb-3">
              {{
                $t("telegramAi.aiCaptureExportCount", {
                  count: aiCaptureMeta.count,
                })
              }}
            </p>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="exportingCapture || loading"
              @click="exportAiCapture"
            >
              <span v-if="exportingCapture">
                <i class="fas fa-spinner fa-spin me-1" aria-hidden="true"></i>
              </span>
              <i v-else class="fas fa-download me-1" aria-hidden="true"></i>
              {{ $t("telegramAi.aiCaptureExportButton") }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import TelegramAiAllowedUsersSelect from "./TelegramAiAllowedUsersSelect.vue";

export default {
  name: "TelegramAiSettings",
  components: { TelegramAiAllowedUsersSelect, ContentSkeleton },
  props: {
    /** En Configuración → pestaña Telegram (sin card contenedora). */
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      saving: false,
      exportingCapture: false,
      aiCaptureMeta: {
        enabled: false,
        retentionDays: 3,
        count: 0,
      },
      allowedSystemUsers: [],
      pickerUsers: [],
      /** Base HTTPS pública opcional (ngrok); se guarda en Settings del tenant. */
      webhookPublicBaseOverride: "",
      cfg: {
        moduleActive: false,
        billingFeatureEnabled: false,
        assistantOperational: false,
        hasBotToken: false,
        hasWebhookSecret: false,
        allowlistConfigured: false,
        allowedUserIds: [],
        webhookUrl: "",
        tenantDb: "",
        explicitPublicApiEnv: false,
        publicWebhookUrlReady: false,
        publicWebhookBase: "",
      },
    };
  },
  async mounted() {
    await this.load();
  },
  methods: {
    isValidHttpsPublicBase(s) {
      const t = String(s || "").trim();
      if (!t || t.length > 400 || !/^https:\/\//i.test(t)) return false;
      try {
        const u = new URL(t);
        if (u.protocol !== "https:") return false;
        const h = (u.hostname || "").toLowerCase();
        if (!h || h === "localhost" || h === "127.0.0.1" || h === "::1") return false;
        return true;
      } catch {
        return false;
      }
    },
    mapUsersForSelect(list) {
      if (!Array.isArray(list)) return [];
      return list.map((u) => ({
        _id: String(u._id),
        name: u.name || "",
        email: u.email || "",
        telegramUsername: u.telegramUsername || "",
      }));
    },
    async load() {
      this.loading = true;
      try {
        const res = await wencesApi.get("/api/telegram-ai/config");
        const d = res.data?.data || {};
        this.cfg = { ...this.cfg, ...d };
        this.cfg.publicWebhookBase = String(d.publicWebhookBase || "").trim();
        this.allowedSystemUsers = this.mapUsersForSelect(d.allowedSystemUsers);
        this.pickerUsers = this.mapUsersForSelect(d.pickerUsers);
        this.webhookPublicBaseOverride = String(d.publicWebhookBase || "").trim();
        const cap = d.aiCaptureExport || {};
        this.aiCaptureMeta = {
          enabled: !!cap.enabled,
          retentionDays:
            cap.retentionDays != null && Number(cap.retentionDays) > 0
              ? Number(cap.retentionDays)
              : 3,
          count: cap.count != null ? Number(cap.count) : 0,
        };
      } catch (e) {
        const msg =
          e.response?.data?.error || e.message || String(this.$t("error"));
        Swal.fire(String(this.$tn("error")), msg, "error");
      } finally {
        this.loading = false;
      }
    },
    async exportAiCapture() {
      this.exportingCapture = true;
      try {
        const tenant = String(this.cfg.tenantDb || "tenant").replace(/[^\w.-]+/g, "_");
        const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const response = await wencesApi.get("/api/telegram-ai/ai-capture-export", {
          responseType: "blob",
        });
        const blob = new Blob([response.data], {
          type: "application/json;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `support-ai-capture-${tenant}-${stamp}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        Swal.fire(
          String(this.$tn("success")),
          String(this.$tn("telegramAi.aiCaptureExportDone")),
          "success"
        );
        await this.load();
      } catch (e) {
        const msg =
          e.response?.data?.error || e.message || String(this.$t("error"));
        Swal.fire(String(this.$tn("error")), msg, "error");
      } finally {
        this.exportingCapture = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        const body = {
          telegramAiAllowedSystemUserIds: this.allowedSystemUsers.map((u) => u._id),
        };
        body.publicWebhookBase = String(this.webhookPublicBaseOverride || "")
          .trim()
          .replace(/\/+$/, "");
        await wencesApi.put("/api/telegram-ai/config", body);
        Swal.fire(
          String(this.$tn("success")),
          String(this.$tn("telegramAi.saved")),
          "success"
        );
        await this.load();
      } catch (e) {
        const msg =
          e.response?.data?.error || e.message || String(this.$t("error"));
        Swal.fire(String(this.$tn("error")), msg, "error");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
